'use client';

import { createContext, useContext } from 'react';
import { useSession, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation'; // Import useRouter
import { Role } from '@/lib/generated/prisma';

type User = {
  id: string;
  email: string | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  role: Role;
  emailVerified?: boolean; // Added emailVerified property
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  logout: () => void; // Changed return type to void
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isAdmin: false,
  logout: () => {}, // Changed default implementation to match
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session, status } = useSession();
  const router = useRouter(); // Get router instance

  const isLoading = status === 'loading';
  const isAuthenticated = !!session?.user;
  const isAdmin = session?.user?.role === Role.ADMIN;

  // Derive user directly from session
  const user: User | null = session?.user
    ? {
        id: session.user.id || '',
        email: session.user.email || null,
        name: session.user.name || null,
        firstName: session.user.firstName || null,
        lastName: session.user.lastName || null,
        role: session.user.role || Role.USER,
        // Get emailVerified properly from both possible locations
        emailVerified: (session.user as any).emailVerified !== undefined 
          ? (session.user as any).emailVerified 
          : (session as any).emailVerified,
      }
    : null;

  const logout = () => {
    // Sign out without automatic redirect, then manually navigate
    signOut({ redirect: false }); 
    router.push('/login?from=logout'); // Add the 'from=logout' parameter
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated, isAdmin, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
